import axios from 'axios';
import { API_BASE_URL, APP_VERSION } from '@/config/envConfig';
import { session } from '@/utils/session';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  // Timeout de 10 segundos para que no se quede colgado
  timeout: 10000
});

// Interceptor para inyectar el Token automáticamente en cada petición
// (S3: el token se lee de la sesión en memoria / cifrada por pestaña,
// nunca de localStorage en claro).
apiClient.interceptors.request.use(
  (config) => {
    const token = session.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Identidad del build: el API la verifica por igualdad exacta y responde 426
    // si el frontend está desactualizado.
    config.headers['X-Client-Version'] = APP_VERSION;
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores globales (ej: token vencido).
// S3: ante un 401 se intenta renovar la sesión una sola vez con POST /refresh
// (rotación de refresh token según contrato del backend) y se re-lanza la
// petición original. Si la renovación falla, se cierra la sesión localmente.
let isRefreshing = false;

// Cierra la sesión local y redirige a login indicando el motivo, para que el
// usuario no se quede "sin datos" sin saber que su sesión terminó.
const endSessionWithMessage = () => {
  session.clearLocal();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login?reason=session_expired';
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    // 426 Upgrade Required: el API exige otra versión del frontend. Se notifica a
    // la UI (App.vue) para mostrar un modal bloqueante que obliga a recargar.
    if (response && response.status === 426 && typeof window !== 'undefined') {
      // Marca global: cubre el caso en que el 426 llegue antes de que App.vue
      // registre su listener (peticiones del arranque).
      window.__nexusClientOutdated = true;
      window.dispatchEvent(new CustomEvent('nexus:client-outdated', {
        detail: response.data || null,
      }));
    }

    const isPublicUrl = ['/login', '/refresh', '/password/']
      .some((prefix) => config?.url?.startsWith(prefix));

    if (response && response.status === 401 && config && !config._retry && !isRefreshing && !isPublicUrl) {
      config._retry = true;
      const refreshToken = session.getRefreshToken();
      if (refreshToken) {
        isRefreshing = true;
        try {
          // Petición con axios puro para no acoplarla a los interceptores.
          const { data } = await axios.post(`${API_BASE_URL}refresh`, { refresh_token: refreshToken });
          if (data && data.token) {
            session.setSession({ token: data.token, refresh_token: data.refresh_token });
            config.headers.Authorization = `Bearer ${data.token}`;
            return apiClient(config);
          }
        } catch (refreshError) {
          console.warn('No se pudo renovar la sesión:', refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    if (response && [401, 403].includes(response.status) && !isPublicUrl) {
      // 401 sin poder renovar = sesión vencida; 403 = sin permisos (posible
      // cambio de rol/permisos). En ambos casos la sesión ya no es confiable:
      // se cierra y se avisa al usuario.
      if (response.status === 401) console.warn('Sesión expirada o inválida');
      if (response.status === 403) console.warn('Acceso denegado (sin permisos)');
      endSessionWithMessage();
    }
    return Promise.reject(error);
  }
);

export default apiClient;